import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

export const useToast = (): ToastContextType => useContext(ToastContext);

export const ToastProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = `toast-${Date.now()}-${++toastIdRef.current}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex flex-col gap-2 items-center">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            className={`px-6 py-3 rounded-lg shadow-lg text-white font-semibold flex items-center gap-3 animate-fadeInUp
              ${toast.type === 'success' ? 'bg-green-600' : ''}
              ${toast.type === 'error' ? 'bg-red-600' : ''}
              ${toast.type === 'info' ? 'bg-blue-600' : ''}
              ${toast.type === 'warning' ? 'bg-yellow-600 text-[var(--theme-text-primary)]' : ''}
            `}
          >
            {toast.type === 'success' && (
              <span aria-hidden="true" className="text-green-200">
                ✓
              </span>
            )}
            {toast.type === 'error' && (
              <span aria-hidden="true" className="text-red-200">
                ✕
              </span>
            )}
            {toast.type === 'info' && (
              <span aria-hidden="true" className="text-blue-200">
                i
              </span>
            )}
            {toast.type === 'warning' && (
              <span aria-hidden="true" className="text-yellow-200">
                !
              </span>
            )}
            <span>{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-2 text-[var(--theme-text-primary)] text-opacity-[0.80] hover:text-white focus:outline-none"
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
